'use client';

import { c as _c } from "react/compiler-runtime";
import { AnalyticsFeatureViewed } from '@cc-shopping/platform-analytics/AnalyticsFeatureViewed';
import { useTrackFeatureInteracted } from '@cc-shopping/platform-analytics/hooks/useTrackFeatureInteracted';
import { MaskIcon } from '@cc-shopping/ui/primitives/MaskIcon';
import { palette } from '@cc-shopping/ui/theme';
import { dynamicWithRetry } from '@cc-shopping/utils/dynamic/dynamicWithRetry';
import { datadogRum } from '@datadog/browser-rum';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { Box, Button, Collapse, IconButton, type SxProps, type Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ActivationFormSkeleton } from 'src/features/approval-odds/ActivationForm/ActivationForm.skeleton';
import { checkmarkPopInSx } from 'src/features/approval-odds/ActivationForm/checkmarkPopIn';
import type { ActivationFormHandle } from 'src/features/approval-odds/ActivationForm/types';
import { ApprovalOddsFunnelProvider, useApprovalOddsFunnelIds } from 'src/features/approval-odds/analytics/ActivationFunnelContext';
import { APPROVAL_ODDS_BANNER_SECTION } from 'src/features/approval-odds/analytics/constants';
import { type ApprovalOddsData, buildApprovalOddsCoverage } from 'src/features/approval-odds/analytics/coverage';
import type { ApprovalOddsResponse } from 'src/features/approval-odds/constants';
import { useApprovalLikelihoodContext } from 'src/features/approval-odds/context';
import { ROUNDUP_PRODUCT_CARDS_SECTION_ID } from 'src/features/roundup-filters/OrderedCardList';
import { v4 as uuid } from 'uuid';
import { waitForScrollToSettle } from './waitForScrollToSettle';
const importActivationForm = () => import('src/features/approval-odds/ActivationForm').then(mod => ({
  default: mod.ActivationForm
}));
const ActivationForm = dynamicWithRetry(importActivationForm, {
  componentName: 'ApprovalOddsActivationForm',
  ssr: false,
  loading: () => <ActivationFormSkeleton />
});
const preloadActivationForm = () => {
  importActivationForm().catch(() => {
    // no-op: dynamicWithRetry's loader handles the real load/retry if a failure occurs here.
  });
};

// Keeps the banner off the very top of the viewport when a product card scrolls it into
// view, mirroring the hero's bottom padding that normally sits above it.
const SCROLL_MARGIN_TOP = '32px';
const HEADLINE = 'See your approval odds';
const BODY_COPY = "No impact to your credit score. We don't save any of the information used to generate your approval odds.";
const CTA_LABEL = 'See my approval odds';
const BANNER_VIEWED_ATTRIBUTES = {
  banner_state: 'collapsed',
  banner_style: 'accordion'
};
type CollapseButtonProps = {
  onClick: () => void;
  ariaExpanded: boolean;
  ariaControls: string;
  sx?: SxProps<Theme>;
};

// Same trigger rendered twice with different responsive properties.
function CollapseButton(t0) {
  const $ = _c(8);
  const {
    onClick,
    ariaExpanded,
    ariaControls,
    sx
  } = t0;
  let t1;
  if ($[0] !== sx) {
    t1 = {
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      height: "40px",
      padding: 0,
      ...sx
    };
    $[0] = sx;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MaskIcon glyph="keyboard-arrow-up" color={palette.blues.main} size="24px" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== ariaControls || $[4] !== ariaExpanded || $[5] !== onClick || $[6] !== t1) {
    t3 = <IconButton onClick={onClick} aria-expanded={ariaExpanded} aria-controls={ariaControls} aria-label="Collapse approval odds" sx={t1}>{t2}</IconButton>;
    $[3] = ariaControls;
    $[4] = ariaExpanded;
    $[5] = onClick;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
type ApprovalOddsBannerProps = {
  marketplaceIds?: string[];
};
export function ApprovalOddsBanner(t0) {
  const $ = _c(4);
  const {
    marketplaceIds: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const marketplaceIds = t2;
  let t3;
  if ($[2] !== marketplaceIds) {
    t3 = <ApprovalOddsFunnelProvider><ApprovalOddsBannerContent marketplaceIds={marketplaceIds} /></ApprovalOddsFunnelProvider>;
    $[2] = marketplaceIds;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function ApprovalOddsBannerContent({
  marketplaceIds
}: Required<ApprovalOddsBannerProps>) {
  const {
    activateApprovalLikelihoods,
    bannerOpenRequestId,
    bannerOpenMarketplaceId
  } = useApprovalLikelihoodContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [activationData, setActivationData] = useState<ApprovalOddsData | null>(null);
  const isActivated = activationData !== null;
  const contentId = useId();
  const activationFormHandleRef = useRef<ActivationFormHandle | null>(null);
  const collapseEnteredRef = useRef(false);
  const bannerActivityIdRef = useRef(uuid());
  const confirmationActivityIdRef = useRef(uuid());
  const trackFeatureInteracted = useTrackFeatureInteracted();
  const funnelIds = useApprovalOddsFunnelIds();
  // AnalyticsFeatureViewed forwards this ref to whichever root element is rendered.
  const bannerRef = useRef<HTMLElement>(null);
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;
  const handleOpen = useCallback((entry: {
    entryPoint: 'banner' | 'product_card';
    marketplaceId?: string;
  }) => {
    funnelIds.current.expandActivityId = trackFeatureInteracted({
      entityName: 'approval_odds_banner_expand',
      entitySection: APPROVAL_ODDS_BANNER_SECTION,
      actionType: 'click',
      parentActivityId: bannerActivityIdRef.current,
      itemCopy: CTA_LABEL,
      additionalAttributes: {
        entry_point: entry.entryPoint,
        ...(entry.marketplaceId && {
          marketplace_id: entry.marketplaceId
        })
      }
    });
    setIsOpen(true);
    setHasOpened(true);
  }, [funnelIds, trackFeatureInteracted]);
  const handleActivated = (approvalOdds: ApprovalOddsResponse) => {
    activateApprovalLikelihoods(approvalOdds);
    setActivationData(buildApprovalOddsCoverage(marketplaceIds, approvalOdds));
  };

  // Handles the scroll up and open banner animation sequence
  useEffect(() => {
    if (!bannerOpenRequestId || isActivated) {
      return;
    }
    const bannerEl = bannerRef.current;
    if (!bannerEl) {
      return;
    }
    let cancelled = false;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    bannerEl.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });
    const openAfterScroll = async () => {
      await waitForScrollToSettle();
      if (cancelled) {
        return;
      }
      if (isOpenRef.current) {
        activationFormHandleRef.current?.focusFirstField();
        return;
      }
      handleOpen({
        entryPoint: 'product_card',
        marketplaceId: bannerOpenMarketplaceId
      });
    };
    openAfterScroll().catch(err => {
      datadogRum.addError(err, {
        context: 'ApprovalOddsBanner scroll-open sequence'
      });
    });
    return () => {
      cancelled = true;
    };
  }, [bannerOpenMarketplaceId, bannerOpenRequestId, isActivated, handleOpen]);

  // Runs after the collapsed "Approval odds active." banner has committed, so the scroll measures
  // the short strip rather than the open form.
  useEffect(() => {
    if (!isActivated || !isMobile) {
      return;
    }
    const cards = document.getElementById(ROUNDUP_PRODUCT_CARDS_SECTION_ID);
    if (!cards) {
      return;
    }
    const prefersReducedMotion_0 = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    cards.scrollIntoView({
      behavior: prefersReducedMotion_0 ? 'auto' : 'smooth',
      block: 'start'
    });
  }, [isActivated, isMobile]);
  if (isActivated) {
    return <AnalyticsFeatureViewed ref={bannerRef} entityName="cc_approval_odds" entitySection={APPROVAL_ODDS_BANNER_SECTION} activityId={confirmationActivityIdRef.current} additionalAttributes={activationData}>
        <Box data-testid="approval-odds-banner" role="status" aria-live="polite" aria-atomic="true" sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: '44px',
        scrollMarginTop: SCROLL_MARGIN_TOP,
        px: 3,
        border: '1px solid',
        borderColor: 'blues.lighter',
        borderRadius: '8px',
        bgcolor: 'blues.lightest'
      }}>
          <MaskIcon glyph="check-circle-rounded" color={palette.blues.main} size="18px" sx={checkmarkPopInSx} />
          <Typography component="p" variant="text" sx={{
          color: 'blues.darkest',
          fontSize: '14px',
          fontWeight: 500
        }}>
            Approval odds active.
          </Typography>
        </Box>
      </AnalyticsFeatureViewed>;
  }
  return <AnalyticsFeatureViewed ref={bannerRef} entityName="approval_odds_banner" entitySection={APPROVAL_ODDS_BANNER_SECTION} activityId={bannerActivityIdRef.current} additionalAttributes={BANNER_VIEWED_ATTRIBUTES}>
      <Box data-testid="approval-odds-banner" sx={{
      border: '1px solid',
      borderColor: 'blues.lighter',
      borderRadius: '8px',
      overflow: 'hidden',
      scrollMarginTop: SCROLL_MARGIN_TOP
    }}>
        <Box sx={{
        display: 'flex',
        // Mobile: chevron sits beside the copy when open, instead of stacking below.
        flexDirection: {
          xs: isOpen ? 'row' : 'column',
          sm: 'row'
        },
        alignItems: {
          xs: isOpen ? 'center' : 'start',
          sm: 'start'
        },
        justifyContent: 'space-between',
        columnGap: 3.5,
        rowGap: 3,
        bgcolor: 'blues.lightest',
        p: {
          xs: 3,
          sm: 3.5
        },
        pb: {
          xs: 4
        }
      }}>
          <Box sx={{
          display: 'flex',
          gap: 3,
          alignSelf: 'center',
          flex: 1,
          minWidth: 0,
          width: '100%'
        }}>
            <Box sx={{
            display: {
              xs: 'none',
              sm: 'flex'
            },
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '51px',
            height: '51px',
            borderRadius: '8px',
            bgcolor: 'blues.lighter-2'
          }}>
              <MaskIcon glyph="lock" color={palette.blues.main} size="24px" />
            </Box>
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            minWidth: 0
          }}>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: {
                xs: '40px',
                sm: 'auto'
              }
            }}>
                <Typography variant="smallHeadline" component="p" sx={{
                fontSize: {
                  xs: '18px',
                  sm: '20px'
                },
                lineHeight: 1.3,
                color: 'blues.darkest'
              }}>
                  {HEADLINE}
                </Typography>
                {isOpen && <CollapseButton onClick={() => setIsOpen(false)} ariaExpanded={isOpen} ariaControls={contentId} sx={{
                display: {
                  xs: 'flex',
                  sm: 'none'
                }
              }} />}
              </Box>
              <Typography variant="text" component="p" sx={{
              fontSize: '14px',
              color: 'blues.darkest'
            }}>
                {BODY_COPY}
              </Typography>
            </Box>
          </Box>
          <Box sx={{
          display: {
            xs: isOpen ? 'none' : 'flex',
            sm: 'flex'
          },
          alignItems: 'start',
          justifyContent: 'center',
          flexShrink: 0,
          width: {
            xs: isOpen ? 'auto' : '100%',
            sm: 'auto'
          }
        }}>
            {isOpen ? <CollapseButton onClick={() => setIsOpen(false)} ariaExpanded={isOpen} ariaControls={contentId} sx={{
            display: {
              xs: 'none',
              sm: 'flex'
            }
          }} /> : <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'blues.extraLight',
            borderRadius: '24px 24px 16px 16px',
            width: '100%'
          }}>
                <Button variant="blue" onClick={() => handleOpen({
              entryPoint: 'banner'
            })} onMouseEnter={preloadActivationForm} onFocus={preloadActivationForm} onTouchStart={preloadActivationForm} aria-expanded={isOpen} aria-controls={contentId} sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              borderRadius: '999px',
              minHeight: '2.5rem',
              textTransform: 'none',
              letterSpacing: '0',
              px: 3,
              py: 1,
              width: {
                xs: '100%',
                sm: 'auto'
              },
              '&:hover': {
                transition: 'background-color 0.15s ease-out',
                '& .mask-icon': {
                  WebkitMaskImage: 'var(--icon-unlock)',
                  maskImage: 'var(--icon-unlock)'
                }
              }
            }}>
                  <MaskIcon glyph="lock" color={palette.common.white} size="24px" />
                  <Typography variant="textBold" sx={{
                color: 'common.white'
              }}>
                    {CTA_LABEL}
                  </Typography>
                </Button>
                <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 0.5,
              px: 1
            }}>
                  <BoltOutlinedIcon sx={{
                fontSize: 18,
                color: 'info.dark'
              }} aria-hidden />
                  <Typography variant="text" sx={{
                color: 'secondary.main',
                fontWeight: 500,
                fontSize: 14
              }}>
                    1-min
                  </Typography>
                </Box>
              </Box>}
          </Box>
        </Box>
        <Collapse in={isOpen} id={contentId} onEntered={() => {
        collapseEnteredRef.current = true;
        activationFormHandleRef.current?.focusFirstField();
      }}>
          <Box sx={{
          borderTop: '1px solid',
          borderColor: 'blues.lighter',
          bgcolor: 'common.white',
          p: {
            xs: 3,
            sm: 3.5
          }
        }}>
            {hasOpened && <ActivationForm onActivated={handleActivated} onReady={handle => {
            activationFormHandleRef.current = handle;
            // Collapse's onEntered may already have fired if this chunk loaded slowly; catch up.
            if (collapseEnteredRef.current) {
              handle.focusFirstField();
            }
          }} />}
          </Box>
        </Collapse>
      </Box>
    </AnalyticsFeatureViewed>;
}